import React, { useEffect, useState } from 'react';
import { ReactComponent as MenuHamburgerIcon } from 'assets/icons/menu-hamburger.svg';
import { ReactComponent as NotificationIcon } from 'assets/icons/notification.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/iconClose.svg';
import { useAuth } from 'auth/context/AuthContext';
import { Section } from './navbar.interfaces';
import { DropdownBodyContainer } from '../dropdown/DropdownBodyContainer';
import { NotificationNavbarDropdown } from '../../app/notifications/components/NotificationaNavbarDropdown/NotificationNavbarDropdown';
import { AccountCard } from './NavbarProfileDropdownComponents/AccountCard';
import {
  AccountDropdown,
  Accounts,
} from './NavbarProfileDropdownComponents/AccountDropdown';
import { NavLink, useLocation } from 'react-router-dom';
import { DASHBOARD, NOTIFICATIONS } from 'utilities/routes';
import Avatar from '../avatarComponent';
import useDropdown from '../dropdown/useDropdown';
import { useGetPage } from 'hooks/useGetPage';
import { pageIds } from 'utilities/constants';
import { useNotificationCount } from '../../notifications/contexts/NotificationsContext';
import ContactSupport from '../footer/ContactSupport';

interface AuthenticatedNavbarProps {
  openNavbarMobile: () => void;
}

interface NavbarMobileProps {
  closeNavbarMobile: () => void;
}

const UnverifiedChildNavbar: React.FC<AuthenticatedNavbarProps> = ({
  openNavbarMobile,
}) => {
  const { profilePicture, user, selectedAccount } = useAuth();
  const location = useLocation();
  const { data: locale, loading } = useGetPage({
    locale: 'en',
    pageId: pageIds.NAVBAR_COMPONENT,
  });

  const [selectedSection, setSelectedSection] = useState<Section>();

  const { openDropdownId, handleDropdownClick, registerDropdownRef } =
    useDropdown();

  const {
    openDropdownId: isOpenNotificationsDropdown,
    handleDropdownClick: handleNotificationsDropdownClick,
    handleHoverOut: handleNotificationsDropdownHoverOut,
  } = useDropdown();

  const isSectionSelected = (section: Section): boolean =>
    selectedSection === section;

  const { internalNotificationCount: notifications } = useNotificationCount();

  useEffect(() => {
    const route = location.pathname.split('/')[1];
    setSelectedSection((route as Section) || Section.Dashboard);
  }, [location]);

  if (loading && !locale) return null;

  return (
    <div className="flex flex-row justify-between gap-6 w-full h-full">
      <div className="hidden desktop:flex desktop:flex-row">
        <div className="my-auto">
          <ContactSupport />
        </div>

        <div
          className="cursor-pointer relative"
          onClick={(event) => {
            event.stopPropagation();
            handleNotificationsDropdownClick('notifications', true);
          }}
          data-testid="notifications-nav"
          data-unread-status={notifications > 0}
        >
          <div className="flex h-[84px] items-center pt-1.5 px-2.5 relative">
            {notifications > 0 && (
              <div className="absolute top-7 right-2 h-2 w-2 rounded-full bg-orange-body-dot" />
            )}
            <NotificationIcon
              className={`h-6 w-6 ${
                isSectionSelected(Section.Notifications) ||
                isOpenNotificationsDropdown === 'notifications'
                  ? 'fill-clc-blue'
                  : 'fill-med-gray'
              }`}
            />

            <div className="absolute bottom-0 w-full h-[30px] navBarHoverDisplay" />
            <div
              className={`flex h-1.5 ${
                isSectionSelected(Section.Notifications) ? 'bg-clc-blue' : ''
              }`}
            />
            {
              <DropdownBodyContainer align={'right'}>
                <NotificationNavbarDropdown
                  isOpen={isOpenNotificationsDropdown === 'notifications'}
                  onClose={handleNotificationsDropdownHoverOut}
                />
              </DropdownBodyContainer>
            }
          </div>
        </div>
      </div>
      <div>
        <div
          className="hidden desktop:flex flex-col cursor-pointer relative"
          onClick={() => handleDropdownClick('my-account')}
          data-testid="profile-nav"
        >
          <div ref={(ref) => registerDropdownRef('my-account', ref)}>
            <div className="flex h-[84px] items-center justify-center pt-1 px-0">
              <AccountCard
                id={selectedAccount || user?.uuid || ''}
                widthClasses="w-[244px]"
                profilePicture={user?.profilePicture}
                name={user?.displayName as string}
                isDropdown
                isFocused={openDropdownId === 'my-account'}
              />
            </div>
            <AccountDropdown
              isOpen={openDropdownId === 'my-account'}
              locale={locale}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col desktop:hidden cursor-pointer relative">
        <NavLink to={NOTIFICATIONS} className="cursor-pointer relative">
          <div className="flex h-[70px] w-[45px] items-center justify-center">
            {notifications > 0 && (
              <div className="absolute top-5 right-2 h-2 w-2 rounded-full bg-orange-body-dot" />
            )}
            <NotificationIcon
              className={`h-6 w-6 ${
                isSectionSelected(Section.Notifications)
                  ? 'fill-clc-blue'
                  : 'fill-med-gray'
              }`}
            />
          </div>
          <div
            className={`absolute bottom-0 w-full h-1.5 ${
              isSectionSelected(Section.Notifications) ? 'bg-clc-blue' : ''
            }`}
          />
        </NavLink>
      </div>
      <div className="flex flex-col desktop:hidden">
        <div
          className="flex h-[70px] w-[45px] items-center justify-center cursor-pointer"
          onClick={openNavbarMobile}
        >
          <Avatar size="xs" imageUrl={profilePicture} key={profilePicture} />
        </div>
      </div>
      <div className="flex desktop:hidden">
        <div className="flex h-[70px] desktop:h-[84px] w-[45px] desktop:w-16 items-center justify-center desktop:pt-1 desktop:px-0">
          <MenuHamburgerIcon
            className="h-8 w-[45px] cursor-pointer"
            onClick={openNavbarMobile}
          />
        </div>
      </div>
    </div>
  );
};

export const UnverifiedChildMobileNavbar: React.FC<NavbarMobileProps> = ({
  closeNavbarMobile,
}) => {
  const location = useLocation();
  const { data: locale, loading } = useGetPage({
    locale: 'en',
    pageId: pageIds.NAVBAR_COMPONENT,
  });

  const [selectedSection, setSelectedSection] = useState<Section>();

  useEffect(() => {
    const route = location.pathname.split('/')[1];
    setSelectedSection((route as Section) || Section.Dashboard);
  }, [location]);

  const isSectionSelected = (section: Section): boolean =>
    selectedSection === section;

  if (loading || !locale) {
    return null;
  }

  return (
    <div className="flex flex-col items-center p-0 bg-white">
      <div className="flex flex-row h-[100px] items-center justify-center p-2.5 gap-2.5">
        <div className="flex flex-col items-center justify-center p-px w-12 h-12">
          <CloseIcon
            className="w-12 h-12 stroke-med-gray"
            onClick={closeNavbarMobile}
          />
        </div>
      </div>

      <div className="p-3.5 w-full">
        <Accounts locale={locale} />
      </div>

      <div className="flex flex-col w-[300px] items-center p-b[60px]">
        <hr className="flex flex-row w-full items-center h-px bg-black-blur" />
        <NavLink
          to={DASHBOARD}
          className="flex flex-row w-full h-[50px] items-center p-0 gap-2.5"
          onClick={closeNavbarMobile}
        >
          <div
            className={`flex flex-row w-1.5 h-full items-start p-0 ${
              isSectionSelected(Section.Dashboard) ? 'bg-clc-blue' : ''
            }`}
          />
          <div
            className={`flex items-center text-base font-bold ${
              isSectionSelected(Section.Dashboard)
                ? 'text-clc-blue'
                : 'text-btn-black'
            }`}
          >
            {locale?.dashboard}
          </div>
        </NavLink>
      </div>
    </div>
  );
};

export default UnverifiedChildNavbar;
